import React, { useState } from 'react';

import AppBar from '@mui/material/AppBar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';


import {useAppCtx} from "./AppCtx";
import {accessCtrl} from "../ctx/AccessCtrl";
import {CxTooltip} from '../cmx/CxTooltip';
import {CxIcon, CxStatusIcon} from '../cmx/CxIcon';
import {CxMenuBox, CxMenuItem} from '../cmx/CxMenu';
import {CxTitle, CxSpacer, CxSubtitle, CxText} from "../cmx/CxTypography"
import {CxInfoDialog} from "../cmx/CxDialog";

import logo from '../res/logo.png';
import {overallStatus} from "../ctx/AppStatus";
import {CxStatusColor} from "../cmx/CxColor";
import {CxGrid} from "../cmx/CxGrid";


export function AppHead() {

    // TODO MAINTENANCE: add error handling if appStatus is not OK

    const { user, isLoggedIn, handleAction, appStatus } = useAppCtx();

    // ----------------------------------------------------------------------------------------------------------------
    // renderMenu
    // ----------------------------------------------------------------------------------------------------------------

    const renderStandardMenu = (callback) => {
        const menu = [
            <CxMenuItem key="home"    label="Home"        iconName="home"    actionName="home"    callback={callback} />,
        ]

        if (accessCtrl.canViewMenuProject) {
            menu.push(
                <CxMenuItem key="project" label="Projekte"    iconName="project" actionName="project" callback={callback} />,
            )
        }

        if (accessCtrl.canViewMenuTask) {
            menu.push(
                <CxMenuItem key="task"    label="Planung"     iconName="plan"    actionName="task"    callback={callback} />
            )
        }

        if (accessCtrl.canViewMenuSearch) {
            menu.push(
                <CxMenuBox key="search"   label="Suche" iconName="search" callback={callback} menuItems={[
                    { label: "Projekte",            iconName: "project",  actionName: "projectSearch" },
                    { label: "Arbeitsvorgänge",     iconName: "plan",     actionName: "taskSearch" },
                    { label: "Garantiefälle",       iconName: "warranty", actionName: "taskWarrantySearch" },
                    { label: "Baustelle Vorb./Stv.",iconName: "site",     actionName: "siteSearch" } ]}
                />
            )
        }

        if (accessCtrl.canViewMenuWorker) {
            menu.push(
                <CxMenuBox key="worker" label="Arbeiter" iconName="crew" callback={callback} menuItems={[
                    { label: "Mitarbeiter",     iconName: "crew",        actionName: "crew" },
                    { label: "Subunternehmer",  iconName: "contractor",  actionName: "contractor" },
                    { label: "Gruppen",         iconName: "team",        actionName: "team" } ]}
                />
            )
        }

        if (accessCtrl.canViewMenuAdmin) {
            menu.push(
                <CxMenuBox key="admin"  label="Verwaltung" iconName="edit" callback={callback} menuItems={[
                    { label: "Arbeitskategorien", iconName: "category",  actionName: "codeCategory" },
                    { label: "Arbeitsvorgänge",   iconName: "task",      actionName: "codeTask" },
                    { label: "Werkzeuge",         iconName: "tool",      actionName: "codeTool" },
                    { label: "Archiv",            iconName: "archive",   actionName: "archive" } ]}
                />
            )
        }

        if (accessCtrl.canViewMenuOper) {
            menu.push(
                <CxMenuBox key="oper"  label="Operation" iconName="operation" callback={callback} menuItems={[
                    { label: "Status",     iconName: "status",  actionName: "operStatus" },
                    { label: "Context",    iconName: "context", actionName: "operContext" },
                    { label: "Cache",      iconName: "cache",   actionName: "operCache" },
                    { label: "Typography", iconName: "typo",    actionName: "operTypo" },
                    { label: "DDL ⚡",      iconName: "schema",  actionName: "operDDL" } ]}
                />
            )
        }

        return menu;
    }

    // ----------------------------------------------------------------------------------------------------------------
    // renderHamburgerMenu
    // ----------------------------------------------------------------------------------------------------------------

    const [anchorElHamburger, setAnchorElHamburger] = useState(null);
    const handleHamburgerMenuOpen = (event) => setAnchorElHamburger(event.currentTarget);
    const handleHamburgerMenuClose = (actionName) => {
        setAnchorElHamburger(null);
        if (handleAction && actionName) {
            handleAction(actionName);
        }
    };

    const renderHamburgerMenu = () => {
        return (
          <>
            <IconButton edge="start" color="inherit" aria-label="menu" sx={{ ml: 2, mr: 2 }}
                        onClick={handleHamburgerMenuOpen} disableRipple>
                <CxIcon name="menu" />
            </IconButton>
            <Menu anchorEl={anchorElHamburger} transitionDuration={0}
                  open={Boolean(anchorElHamburger)} onClose={() => handleHamburgerMenuClose(null)} >

                { renderStandardMenu(handleHamburgerMenuClose) }

            </Menu>
          </>
        )
    }

    // ----------------------------------------------------------------------------------------------------------------
    // renderUserLogout
    // ----------------------------------------------------------------------------------------------------------------

    const [openStatus, setOpenStatus] = useState(false);

    const getUserInfo = () => {
        return `${user.firstname} ${user.lastname} (${user.role})`;
    }

    const getUserInitials = () => {
        if (user.initials) {
            return user.initials;
        }

        // Extract initials from first and last name
        const first = user.firstname?.charAt(0) || "";
        const last = user.lastname?.charAt(0) || "";

        return `${first}${last}`.toUpperCase();
    }

    const renderStatusContent = (status, statusColor) => {
        return (
            <Box sx={{ width: '100%' }}>
                <CxGrid container spacing={2}>
                    <CxGrid size="xl">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CxStatusIcon status={status} />
                            <Box sx={{ ml: 1 }}>
                                <CxText sx={{ color: statusColor }} >{status}</CxText>
                            </Box>
                        </Box>
                    </CxGrid>
                    <CxGrid size="xl">
                        <CxSubtitle>Details</CxSubtitle>
                    </CxGrid>
                    <CxGrid size="xl">
                        <pre>{JSON.stringify(appStatus, null, 2)}</pre>
                    </CxGrid>

                </CxGrid>
            </Box>
        );
    }

    const renderUserLogout = () => {
        const status = overallStatus(appStatus);
        const statusColor = CxStatusColor[status];

        return (
            <>
                <CxSpacer />
                <CxInfoDialog
                    open={openStatus}
                    title="Status"
                    content={renderStatusContent(status, statusColor)}
                    handleInfo={() => setOpenStatus(false)}
                />

                <CxTooltip title="Status" placement="bottom-end">
                    <IconButton edge="end" color="inherit" aria-label="Status" onClick={() => setOpenStatus(true)}>
                        <CxIcon name="status" sx={{ color: statusColor }} />
                    </IconButton>
                </CxTooltip>

                <Typography sx={{color: 'gray', ml:2, mr:1}}>|</Typography>

                <CxTooltip title={getUserInfo()} placement="bottom-end">
                    <Typography>{getUserInitials()}</Typography>
                </CxTooltip>

                <CxTooltip title="Abmelden" placement="bottom-end">
                    <IconButton edge="end" color="inherit" aria-label="Abmelden" onClick={() => handleAction('userLogout')}>
                        <CxIcon name="logout" sx={{ color: '#d65d51' }} />
                    </IconButton>
                </CxTooltip>
            </>
        )
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render menu or welcome
    // ----------------------------------------------------------------------------------------------------------------

    const renderMenu = () => {
        return (
            <>
                { isMobile ? renderHamburgerMenu() : renderStandardMenu(handleAction) }
                { renderUserLogout() }
            </>
        );
    }

    const renderWelcome = () => {
        return (
            <CxTitle sx={{marginLeft: 1}}>Bauplanung</CxTitle>
        );
    }

    // ----------------------------------------------------------------------------------------------------------------
    // render response
    // ----------------------------------------------------------------------------------------------------------------

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static" sx={{ backgroundColor: 'white', color: 'black'}}>
                <Toolbar className="appHeadToolbar" >

                        <Box component="img" alt="Logo" src={logo}
                             sx={{ height: '40px', marginLeft: 1 }} />

                        { isLoggedIn ? renderMenu() : renderWelcome() }

                </Toolbar>
            </AppBar>
        </Box>
    );
}
